// CameraStream -- base in-app camera harness for the WIN Field PWA.
//
// Wraps raw getUserMedia so the tasker NEVER leaves the app (no native camera
// handoff -- which backgrounds the PWA and gets the capture killed on iOS).
// Owns the MediaStream lifecycle, frame capture (one reusable canvas), camera
// flip, and the three discrete rear-lens modes -- Wide | 1x | 2x -- mirroring a
// phone camera. The barcode scanner stays on Html5Qrcode; this class is for the
// photo-capture surfaces (see field_photo_camera_controller).
//
// Real still capture (card #3487): where the browser exposes ImageCapture,
// captureFrame() pulls an actual full-resolution sensor still via
// takePhoto() off the SAME already-open track instead of grabbing a frame from
// the live video -- this is NOT a native-app handoff, it operates on the
// existing getUserMedia track, so none of the backgrounding risk above
// applies. Safari/iOS never implemented ImageCapture, so that platform keeps
// today's canvas frame-grab exactly as it is, feature-detected (not UA
// sniffed) via supportsStillCapture().
//
// Lens-mode model (the fix for the back-to-front bug):
//   - "1x"   = the DEFAULT main rear camera. Plain { facingMode: 'environment' },
//              NO pinned deviceId, zoom reset. This is the baseline view.
//   - "wide" = the ultra-wide rear camera, selected by its own deviceId detected
//              from enumerateDevices() back-camera labels (ultra/wide/0.5).
//   - "2x"   = the main rear camera (same stream as 1x) with applyConstraints
//              advanced zoom: 2, clamped to the track's getCapabilities().zoom.max.
//
// Permission / availability failures throw a CameraStreamError carrying a
// stable `code` so callers can branch (e.g. fall back to the native file
// input). Codes mirror the getUserMedia DOMException names plus our own
// "insecure-context" / "unsupported".

// Ideal capture resolution requested from getUserMedia, both axes (the browser
// fits the request to the sensor's real aspect ratio). Without an explicit
// width/height the browser negotiates a conservative video-call default (e.g.
// 640x360) tuned for smooth preview, nowhere near the sensor's still capability
// -- taskers were getting 359x640 captures (card #2599). `ideal` is a
// preference, not a requirement: low-end hardware negotiates its best
// available match instead of failing.
//
// Was 2048 (matching field_image_compressor.js's top preset) until card
// #3510: on many real-world Android/Chrome camera stacks this constraint
// doesn't just cap the PREVIEW track -- it throttles the camera hardware's
// overall operating mode, including what ImageCapture.takePhoto() (card
// #3487's real still-capture path) can return, even though the W3C spec
// says still-photo capability should be independent of the negotiated video
// resolution. Confirmed on Jay's own device: a real still capture logged an
// exact 2048x2048, on both axes, matching this constant precisely -- not
// something an unconstrained 9MP+ sensor would ever produce on its own.
// 4096 is comfortably above the long edge of a typical modern phone sensor
// (most rear sensors top out well under this on their long edge once
// aspect-fitted), so it gives the hardware room to negotiate its best still
// mode without pinning it to a square ceiling. It no longer needs to match
// field_image_compressor.js's top preset -- that preset still caps the
// FINAL compressed output; this constant only bounds the CAPTURE input.
//
// Card #3518 briefly raised the compressor's top rung to 4096, making this
// constant numerically EQUAL to it. The 2026-09-22 photo pipeline review
// (zenith/features/field-app README) brought the compressor back to a
// byte-targeted ladder with a 2560 top rung, so capture once again
// comfortably exceeds compression -- the original #3510 margin is restored.
// This constant deliberately did NOT move with it: the invariant is that the
// capture input is never LESS than what the compressor can use. If the
// compressor's top rung is ever raised past 4096 without raising this
// constant too, the compressor would once again be starved of real detail,
// same failure mode #3510 fixed.
export const IDEAL_CAPTURE_DIMENSION = 4096

export class CameraStreamError extends Error {
  constructor(code, message) {
    super(message || code)
    this.name = "CameraStreamError"
    this.code = code
  }
}

// ── Focus (tap-to-focus) -- free functions over a raw MediaStreamTrack ──────
//
// Extracted to standalone exports (card #4495) so a controller that doesn't
// use the CameraStream class -- field_odometer_camera_controller.js, which
// drives its own raw getUserMedia stream -- can still reuse the exact same
// honest-gating logic instead of duplicating it. CameraStream's own
// focusCapability()/_applyFocus() (below) are thin wrappers over these.

// Focus capability of a given MediaStreamTrack: true when the track advertises
// a focus mode we can drive a tap with ('single-shot' or 'manual'), else null.
//
// Why NOT also require `pointsOfInterest` in getCapabilities(): the Image
// Capture spec defines pointsOfInterest only on MediaTrackConstraintSet and
// MediaTrackSettings -- it is NOT a member of MediaTrackCapabilities at all, so
// `"pointsOfInterest" in caps` can never be a reliable gate (card #4495
// follow-on: a Samsung S25 FE in Chrome fell into the fallback on every tap).
// There is no capability flag for it; the honest test is to apply it and read
// getSettings().pointsOfInterest back (see applyFocusToTrack). iOS Safari
// exposes no focusMode at all, so this still returns null there by
// construction -- no UA sniff.
export function focusCapabilityForTrack(track) {
  const focusModes = track?.getCapabilities?.()?.focusMode
  if (!Array.isArray(focusModes)) return null
  return focusModes.includes("single-shot") || focusModes.includes("manual") ? true : null
}

// How long a tap-driven focus lock (single-shot / manual) is held before we
// hand the lens back to continuous autofocus. Single-shot and manual both leave
// focus where it landed; without this the camera would stay stuck on the first
// tap for the rest of the session.
export const FOCUS_RESTORE_DELAY_MS = 2500
const focusRestoreTimers = new WeakMap() // track -> pending restore timer id

function clampUnit(v) {
  return Math.min(1, Math.max(0, v))
}

function focusSettingsSnapshot(track) {
  try {
    const settings = track?.getSettings?.() || {}
    return {
      focusMode: settings.focusMode ?? null,
      focusDistance: typeof settings.focusDistance === "number" ? Math.round(settings.focusDistance * 1000) / 1000 : null,
      pointsOfInterest: Array.isArray(settings.pointsOfInterest) && settings.pointsOfInterest.length > 0
        ? settings.pointsOfInterest.map((p) => ({ x: Math.round(p.x * 100) / 100, y: Math.round(p.y * 100) / 100 }))
        : null
    }
  } catch {
    return { focusMode: null, focusDistance: null, pointsOfInterest: null }
  }
}

// After a tap-driven lock, go back to continuous autofocus. Re-armed on every
// tap (the previous pending restore is cancelled) so rapid repeat taps keep the
// latest lock for the full delay. Best-effort, never throws.
function scheduleContinuousRestore(track) {
  clearTimeout(focusRestoreTimers.get(track))
  const modes = track?.getCapabilities?.()?.focusMode
  if (!Array.isArray(modes) || !modes.includes("continuous")) return
  const timer = setTimeout(async () => {
    focusRestoreTimers.delete(track)
    try {
      if (track.readyState && track.readyState !== "live") return
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] })
    } catch {
      // Track gone or constraint refused -- nothing to restore.
    }
  }, FOCUS_RESTORE_DELAY_MS)
  focusRestoreTimers.set(track, timer)
}

// Apply a tap focus point to a MediaStreamTrack. x/y are normalised 0-1 within
// the native video frame (0,0 = top-left). Tries 'single-shot' first (one
// autofocus sweep at the point -- what a camera app does), then 'manual', each
// only if the track advertises it. Advanced constraints a browser can't honour
// are silently ignored rather than rejected, so a resolved applyConstraints()
// proves nothing; each attempt therefore reads getSettings() back and records
// whether pointsOfInterest actually took (`confirmed`).
//
// Resolves { applied, attempts }. `applied` is true when an attempt resolved
// without throwing; `attempts` is a compact, log-safe record of every variant
// tried (variant, resolved, confirmed, error name/message, settings read-back).
// Stops at the first CONFIRMED variant. Never throws.
export async function applyFocusToTrack(track, x, y) {
  const attempts = []
  if (!track || focusCapabilityForTrack(track) !== true) return { applied: false, attempts }

  const modes = track.getCapabilities().focusMode
  const point = { x: clampUnit(x), y: clampUnit(y) }
  const variants = ["single-shot", "manual"].filter((m) => modes.includes(m))
  let applied = false

  for (const variant of variants) {
    const attempt = { variant, resolved: false, confirmed: false, error: null }
    try {
      await track.applyConstraints({ advanced: [{ focusMode: variant, pointsOfInterest: [point] }] })
      attempt.resolved = true
      applied = true
    } catch (err) {
      attempt.error = `${err?.name || "Error"}: ${String(err?.message || "").slice(0, 120)}`
    }
    attempt.settings = focusSettingsSnapshot(track)
    const poi = attempt.settings.pointsOfInterest?.[0]
    attempt.confirmed = attempt.resolved && !!poi && Math.abs(poi.x - point.x) <= 0.02 && Math.abs(poi.y - point.y) <= 0.02
    attempts.push(attempt)
    if (attempt.confirmed) break
  }

  if (applied) scheduleContinuousRestore(track)
  return { applied, attempts }
}

// Best-effort fallback for phones with no tap-to-focus (iOS Safari, some
// Android): re-assert continuous autofocus so the lens re-evaluates the scene.
// Only attempted when the track honestly advertises 'continuous' in
// focusMode. Resolves true ONLY when the browser accepted the constraint --
// that is "the nudge was sent", never "the image is now sharp", which we
// cannot verify. Never throws.
export async function nudgeAutofocusOnTrack(track) {
  if (!track) return false
  try {
    const focusModes = track.getCapabilities?.()?.focusMode
    if (!Array.isArray(focusModes) || !focusModes.includes("continuous")) return false
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] })
    return true
  } catch {
    return false
  }
}

// One entry point for a viewfinder tap. Capable tracks get the tap focus; if no
// variant was even accepted, or the track is not capable, it falls through to
// the continuous-autofocus nudge. `mode` is "supported" when a focus variant
// was applied, else "fallback". `success` = a constraint was accepted (never a
// claim that the image is sharp); `confirmed` = settings read-back showed our
// point of interest took effect. `attempts` feeds telemetry.
export async function focusOrNudgeTrack(track, x, y) {
  if (focusCapabilityForTrack(track) === true) {
    const { applied, attempts } = await applyFocusToTrack(track, x, y)
    if (applied) {
      return { mode: "supported", success: true, confirmed: attempts.some((a) => a.confirmed), attempts }
    }
    return { mode: "fallback", success: await nudgeAutofocusOnTrack(track), confirmed: false, attempts }
  }
  return { mode: "fallback", success: await nudgeAutofocusOnTrack(track), confirmed: false, attempts: [] }
}

// Snapshot of what the track says about focus, for the "Focus capabilities"
// log event on camera open. Compact and non-sensitive: capability key names and
// values, settings read-back, and the camera label (hardware name, not PII).
export function focusDiagnosticsForTrack(track) {
  try {
    const caps = track?.getCapabilities?.() || {}
    const settings = track?.getSettings?.() || {}
    const range = (r) => (r && typeof r === "object" && "min" in r ? { min: r.min, max: r.max } : r ?? null)
    return {
      label: track?.label || null,
      capsFocusMode: Array.isArray(caps.focusMode) ? caps.focusMode : null,
      capsFocusDistance: range(caps.focusDistance),
      capsHasPointsOfInterest: "pointsOfInterest" in caps,
      capsKeys: Object.keys(caps).sort(),
      settingsFocusMode: settings.focusMode ?? null,
      settingsFocusDistance: typeof settings.focusDistance === "number" ? settings.focusDistance : null,
      settingsHasPointsOfInterest: "pointsOfInterest" in settings,
      settingsKeys: Object.keys(settings).sort(),
      width: settings.width ?? null,
      height: settings.height ?? null,
      facingMode: settings.facingMode ?? null,
      focusCapable: focusCapabilityForTrack(track) === true
    }
  } catch {
    return { error: "diagnostics_failed" }
  }
}

export class CameraStream {
  constructor() {
    this._stream = null
    this._video = null
    this._facingMode = "environment"
    this._canvas = null // one reusable canvas across captures (low-end memory)
    this._deviceId = null // active video input device, when pinned to a lens
    this._backCameras = [] // enumerated back/rear video inputs (for wide mode)
    this._mode = "1x" // current discrete lens mode: "wide" | "1x" | "2x"
    this._lastFrameWidth = null // video.videoWidth negotiated at last captureFrame (card #2630)
    this._lastFrameHeight = null // video.videoHeight negotiated at last captureFrame (card #2630)
    this._lastCaptureMethod = null // "still" | "video-frame" at last captureFrame (card #3487)
    this._lastStillCaptureError = null // set when takePhoto() fell back to video-frame (card #3487)
    this._lastCaptureWidth = null // actual captured content width at last captureFrame -- the still bitmap's width when method is "still", else the video frame width (card #3510)
    this._lastCaptureHeight = null // actual captured content height at last captureFrame -- see _lastCaptureWidth (card #3510)
    this._lastStartDiagnostics = null // snapshot taken right after the last start() resolved -- see startDiagnostics() (card #3794)
  }

  get facingMode() {
    return this._facingMode
  }

  get deviceId() {
    return this._deviceId
  }

  get mode() {
    return this._mode
  }

  // The dimensions of what was ACTUALLY captured at the last captureFrame()
  // call. When captureMethod() is "still", this is the real takePhoto()
  // bitmap's width/height -- the genuine sensor resolution, independent of
  // (and usually much larger than) the negotiated video track. When it's
  // "video-frame" (Safari/iOS, or a takePhoto() failure), the video track's
  // negotiated video.videoWidth/videoHeight IS the real capture, unchanged
  // from before card #3487. Originally this only ever reported the video
  // track's dimensions (card #2630) -- that was correct back when the video
  // frame was always the capture, but left every still-capture "Frame
  // captured" log reporting the wrong number once #3487 shipped. Fixed as
  // part of card #3510, alongside raising IDEAL_CAPTURE_DIMENSION, so we can
  // actually confirm what real stills measure on tasker devices. Null until
  // a frame has been captured.
  frameDimensions() {
    return this._lastCaptureWidth ? { width: this._lastCaptureWidth, height: this._lastCaptureHeight } : null
  }

  // Start the camera on the given <video> element. Resolves once the stream is
  // attached and playing. Rejects with a CameraStreamError on permission denial,
  // missing device, insecure context, or unsupported browser.
  //
  // Pass deviceId to bind a specific physical lens (used by wide mode). When
  // deviceId is set it takes precedence over facingMode.
  async start(videoEl, { facingMode = "environment", deviceId = null } = {}) {
    if (!videoEl) throw new CameraStreamError("unsupported", "No video element supplied")

    this._video = videoEl
    this._facingMode = facingMode
    this._deviceId = deviceId

    if (!window.isSecureContext) {
      throw new CameraStreamError("insecure-context", "Camera requires a secure (https) context")
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new CameraStreamError("unsupported", "getUserMedia is not available")
    }

    // Stop any prior stream first so we never leak tracks across restarts.
    this.stop()

    // Both lens paths carry the same ideal resolution request -- see
    // IDEAL_CAPTURE_DIMENSION. Zoom changes later ride applyConstraints with
    // ONLY `advanced: [{ zoom }]` (no width/height), which in practice leaves
    // the negotiated resolution untouched, so the two don't conflict.
    const resolution = {
      width: { ideal: IDEAL_CAPTURE_DIMENSION },
      height: { ideal: IDEAL_CAPTURE_DIMENSION }
    }
    const videoConstraints = deviceId
      ? { deviceId: { exact: deviceId }, ...resolution }
      : { facingMode, ...resolution }

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints })
    } catch (err) {
      // DOMException names: NotAllowedError, NotFoundError, NotReadableError,
      // OverconstrainedError, SecurityError, AbortError.
      throw new CameraStreamError(err?.name || "unknown", err?.message || String(err))
    }

    this._stream = stream
    videoEl.srcObject = stream
    // Mirror the front-facing lens so the preview reads like an actual mirror
    // (raise your right hand, it appears on the right) -- matching every
    // native camera app. Rear lens draws unmirrored, as normal. Re-applied on
    // every start() call, so flip()'s subsequent start() (see flipCamera in
    // field_photo_camera_controller.js) always lands the correct transform;
    // flip() itself only tears the stream down (no frame visible in between),
    // so it needs no mirror handling of its own (card #3485).
    videoEl.style.transform = facingMode === "user" ? "scaleX(-1)" : ""
    videoEl.setAttribute("playsinline", "")
    videoEl.muted = true
    let playResolved = true
    let playError = null
    try {
      await videoEl.play()
    } catch (err) {
      // Autoplay can reject in some browsers even with a live stream; the frame
      // is still capturable, so this is non-fatal.
      playResolved = false
      playError = { name: err?.name || "unknown", message: err?.message || String(err) }
    }

    // Diagnostic snapshot (card #3794 -- "camera preview shows black on some
    // iPhones"): taken right here, at the exact moment start() would
    // otherwise silently swallow a play() rejection, so a black-viewfinder
    // report can be cross-checked against real evidence instead of a guess.
    // readyState/videoWidth/videoHeight are read synchronously right after
    // play() settles (resolved OR rejected) -- a 0x0 videoWidth/videoHeight
    // here would mean no frame has been decoded at all, a DIFFERENT failure
    // than a decoded-but-not-rendered black preview. The track's own
    // readyState/muted are read independently of the video element, since a
    // track can be "live" while the element itself never renders a frame.
    const track = this._track()
    this._lastStartDiagnostics = {
      playResolved,
      playError,
      readyState: videoEl.readyState,
      videoWidth: videoEl.videoWidth,
      videoHeight: videoEl.videoHeight,
      trackReadyState: track?.readyState ?? null,
      trackMuted: track?.muted ?? null
    }

    return stream
  }

  // The diagnostic snapshot taken right after the most recent start() call's
  // videoEl.play() settled (see the comment in start()). Null before any
  // start() call has run. Read by the controller's _logCameraOpen so it
  // rides the SAME "Camera opened" logEvent() call that already surfaces
  // the other open-time diagnostics (card #3794).
  startDiagnostics() {
    return this._lastStartDiagnostics
  }

  // ── Discrete lens modes ────────────────────────────────────────────────────

  // Switch to one of the three discrete lens modes, restarting the stream as
  // needed. The CALLER passes the same video element it started on. Resolves to
  // the mode actually applied. Best-effort: an unsupported request falls back to
  // the closest live state without throwing (the caller re-syncs the controls).
  //
  //   "1x"   -> default environment stream, no pinned deviceId, zoom reset to base.
  //   "wide" -> pinned to the detected ultra-wide back deviceId, zoom reset.
  //   "2x"   -> default environment stream (same as 1x) + advanced zoom 2 (clamped).
  async selectMode(videoEl, requested) {
    const mode = requested === "wide" || requested === "2x" ? requested : "1x"

    if (mode === "wide") {
      const deviceId = this.wideDeviceId()
      if (!deviceId) return this._mode // no wide lens -- leave current mode untouched
      await this.start(videoEl, { deviceId })
      this._mode = "wide"
      await this._resetZoomToBase()
      return this._mode
    }

    // "1x" and "2x" both ride the DEFAULT main rear stream (plain environment,
    // no pinned deviceId). Only restart the base stream when we're not already
    // on it (e.g. coming back from wide), so 1x<->2x is just a zoom change.
    if (this._mode === "wide" || this._deviceId) {
      await this.start(videoEl, { facingMode: "environment" })
    }

    if (mode === "2x") {
      await this._applyZoom(2)
      this._mode = "2x"
    } else {
      await this._resetZoomToBase()
      this._mode = "1x"
    }
    return this._mode
  }

  // True when the active track can reach 2x zoom (capability max >= 2). 2x mode
  // is gated on this so we never show a dead button.
  supportsTwoX() {
    const cap = this.zoomCapability()
    return !!cap && cap.max >= 2
  }

  // True when a distinct Wide candidate is identified by EITHER path -- a label
  // match (ultra/wide/0.5) OR the generic-index fallback for Android's "camera N,
  // facing back" labels. Gates the Wide button.
  supportsWide() {
    return this.wideDeviceId() != null
  }

  // ── Zoom (internal -- driven by the discrete modes, not a slider) ───────────

  // Zoom capability of the active track: { min, max, step } or null when the
  // browser/hardware doesn't report zoom (typical on iOS Safari). Read AFTER
  // start(), once the track is live.
  zoomCapability() {
    const track = this._track()
    const caps = track?.getCapabilities?.()
    if (!caps || caps.zoom == null) return null
    const { min, max, step } = caps.zoom
    if (typeof min !== "number" || typeof max !== "number" || max <= min) return null
    return { min, max, step: step || (max - min) / 100 }
  }

  // Apply a zoom value to the active track, clamped to capability. No-op
  // (resolves false) when zoom isn't supported. Best-effort.
  async _applyZoom(value) {
    const track = this._track()
    const cap = this.zoomCapability()
    if (!track || !cap) return false
    const clamped = Math.min(cap.max, Math.max(cap.min, value))
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped }] })
      return true
    } catch {
      return false
    }
  }

  // Reset zoom to the capability minimum (the lens's base 1x). No-op when zoom
  // isn't reported.
  async _resetZoomToBase() {
    const cap = this.zoomCapability()
    if (!cap) return false
    return this._applyZoom(cap.min)
  }

  // ── Focus (tap-to-focus, Chrome/Android only -- card #3488) ────────────────

  // Focus capability of the active track -- see focusCapabilityForTrack()
  // below for the full explanation. Read AFTER start(), once the track is
  // live.
  focusCapability() {
    return focusCapabilityForTrack(this._track())
  }

  // True when the active track advertises a focus mode we can drive a tap
  // with (see focusCapabilityForTrack).
  supportsFocus() {
    return this.focusCapability() === true
  }

  // Apply a manual focus point to the active track -- see applyFocusToTrack()
  // below for the full explanation.
  async _applyFocus(x, y) {
    return (await applyFocusToTrack(this._track(), x, y)).applied
  }

  // Tap handler entry point -- supported focus, or the autofocus-nudge
  // fallback. See focusOrNudgeTrack().
  async focusOrNudge(x, y) {
    return focusOrNudgeTrack(this._track(), x, y)
  }

  // The active video track of the live stream, or null.
  _track() {
    return this._stream?.getVideoTracks?.()[0] || null
  }

  // ── Real still capture (ImageCapture.takePhoto(), Android/Chrome only -- card #3487) ──

  // True when the browser exposes the ImageCapture API -- a Web API that
  // operates on the SAME already-open MediaStreamTrack from getUserMedia (not
  // a native-app handoff, see file header). Feature-detected by presence
  // ("ImageCapture" in window), not a UA sniff -- same honest-gating
  // convention as focusCapability()/wideDeviceId()/zoomCapability(). Safari/
  // iOS never implemented ImageCapture, so this is false there by
  // construction and captureFrame() falls back to the existing canvas
  // frame-grab, unchanged. Also future-proofs the code for free if/when
  // Safari ever ships support.
  supportsStillCapture() {
    return typeof window !== "undefined" && "ImageCapture" in window
  }

  // Which path the last captureFrame() call actually used: "still" (a real
  // sensor photo via takePhoto()) or "video-frame" (the canvas grab off the
  // live stream -- always true on iOS, or on Android when takePhoto() failed
  // and captureFrame() silently fell back). Diagnostic surface for the
  // controller's logEvent call. Null until a frame has been captured.
  captureMethod() {
    return this._lastCaptureMethod
  }

  // Set when the last captureFrame() call attempted the still-capture path
  // but takePhoto() (or the follow-on decode) threw, and captureFrame()
  // silently fell back to the video-frame path instead of surfacing an
  // error. Null on a clean still capture, a clean video-frame-only capture
  // (ImageCapture unsupported), or before any capture has run.
  stillCaptureFallbackReason() {
    return this._lastStillCaptureError
  }

  // Pull a real sensor still off the active track and decode it to an
  // ImageBitmap. Uses takePhoto() -- NOT grabFrame(), a similarly-named but
  // different ImageCapture method that returns an ImageBitmap off the LIVE
  // VIDEO track (the same fidelity as the canvas frame-grab we already have,
  // no improvement at all). Do not "simplify" this call to grabFrame().
  // Throws on any failure -- the caller (captureFrame) decides how to
  // fall back.
  async _captureStillBitmap() {
    const track = this._track()
    if (!track) throw new Error("no active video track")
    const imageCapture = new window.ImageCapture(track)
    const blob = await imageCapture.takePhoto()
    return createImageBitmap(blob)
  }

  // Centre-crop rect (sx, sy, sw, sh) that fills `target` aspect from a
  // sourceWidth x sourceHeight image, cropping the overflow from the centre --
  // the same object-fit: cover math captureFrame() has always used against the
  // live video frame, generalised so the still-capture path (a differently-
  // shaped real sensor photo) can share it exactly.
  _centreCropRect(sourceWidth, sourceHeight, target) {
    let sw, sh, sx, sy
    if (sourceWidth / sourceHeight > target) {
      // Source wider than target -- full height shown, sides cropped.
      sh = sourceHeight
      sw = sourceHeight * target
      sx = (sourceWidth - sw) / 2
      sy = 0
    } else {
      // Source taller/narrower than target -- full width shown, top/bottom cropped.
      sw = sourceWidth
      sh = sourceWidth / target
      sx = 0
      sy = (sourceHeight - sh) / 2
    }
    return { sx, sy, sw, sh }
  }

  // Encode the reusable canvas to a Blob. Prefers WebP, falls back to JPEG
  // (Safari historically lacks WebP toBlob support) -- same encode step
  // captureFrame() has always used, extracted so both the still and
  // video-frame paths share it.
  _encodeCanvas(canvas, type, quality) {
    return new Promise((resolve, reject) => {
      const done = (blob) => {
        if (blob) resolve(blob)
        else reject(new CameraStreamError("encode-failed", "Frame encoding failed"))
      }
      canvas.toBlob((blob) => {
        // Safari can't encode WebP and hands back a PNG (often 14-20 MB)
        // instead of null, so only accept the blob when it is the type we
        // asked for; anything else is re-encoded as JPEG.
        if (blob && blob.type === type) done(blob)
        else canvas.toBlob(done, "image/jpeg", quality)
      }, type, quality)
    })
  }

  // ── Back-camera enumeration (for wide detection + diagnostics) ──────────────

  // Enumerate video input devices and cache the back/rear-facing ones. Returns
  // the list of back cameras (possibly empty). Must be called after a stream has
  // been granted at least once, or labels come back blank (browser privacy).
  async refreshBackCameras() {
    if (!navigator.mediaDevices?.enumerateDevices) {
      this._backCameras = []
      return this._backCameras
    }
    let devices
    try {
      devices = await navigator.mediaDevices.enumerateDevices()
    } catch {
      this._backCameras = []
      return this._backCameras
    }
    const videoInputs = devices.filter((d) => d.kind === "videoinput")
    // Labels are only populated once permission is granted. Heuristic: keep
    // inputs whose label hints "back"/"rear"/"environment". If NO labels carry a
    // hint (e.g. labels empty pre-permission), we can't distinguish lenses, so
    // we report none -- Wide stays hidden rather than guessing wrong.
    const labelled = videoInputs.filter((d) => d.label)
    if (!labelled.length) {
      this._backCameras = []
      return this._backCameras
    }
    const back = labelled.filter((d) => /back|rear|environment/i.test(d.label))
    this._backCameras = back.length ? back : []
    return this._backCameras
  }

  // The cached back cameras (after refreshBackCameras). Read-only view for the
  // controller's diagnostic logging.
  backCameras() {
    return this._backCameras
  }

  // True when more than one back camera was detected (so a wide/normal split is
  // meaningful). Single-lens devices and iOS (lenses not separately enumerable)
  // return false.
  hasMultipleBackCameras() {
    return this._backCameras.length > 1
  }

  // Pick the deviceId of the ultra-wide back lens. Three paths, in priority order:
  //
  //   1. ULTRA LABEL: a back-camera label naming "ultra" (iPhone "Back Ultra
  //      Wide Camera").
  //   2. WIDE LABEL: "wide"/"0.5", excluding "dual" virtual cameras (card #4110:
  //      "Back Dual Wide Camera" is the normal 1x lens, not the Wide lens).
  //   3. GENERIC-INDEX FALLBACK: Android's Chrome often labels back cameras
  //      generically as "camera <N>, facing back" with NO wide/ultra hint. Parse
  //      the index N from each label; the LOWEST index is the main/1x lens. Pick
  //      a DIFFERENT back camera as the Wide candidate -- on a 2-back-camera phone
  //      that's simply the other one; with 3+ indexed back cameras, the
  //      lowest-index NON-main one.
  //
  // Returns null when there aren't multiple back cameras or no distinct candidate
  // can be identified (honest gating -- no blind fallback to the main lens).
  //
  // Caveat: on a 2-back-camera phone the non-main lens is almost always the
  // ultra-wide, but without FOV data this is a best-effort guess. If it turns out
  // to be a telephoto/macro on some device, the user simply sees a "Wide" that
  // isn't wider -- acceptable, and the diagnostic log lets us correct per-device.
  wideDeviceId() {
    return this.wideSelection()?.deviceId || null
  }

  // The chosen Wide camera plus WHY, for the diagnostics log (card #4110).
  // Returns { deviceId, label, reason } or null. Reasons:
  //   "ultra-label"    -- a label naming the Ultra Wide lens (iPhone "Back Ultra
  //                       Wide Camera"). Always preferred.
  //   "wide-label"     -- a wide / 0.5 label that is NOT a "dual" virtual camera.
  //                       iOS "Back Dual Wide Camera" is the normal 1x lens (a
  //                       wide + ultra wide pair presented as one), so it is
  //                       never treated as the Wide lens.
  //   "generic-index"  -- Android "camera <N>, facing back" fallback.
  wideSelection() {
    if (!this.hasMultipleBackCameras()) return null

    // 1. The real ultra-wide lens, by name.
    const ultra = this._backCameras.find((d) => /ultra/i.test(d.label))
    if (ultra) return { deviceId: ultra.deviceId, label: ultra.label, reason: "ultra-label" }

    // 2. Other wide / 0.5 labels, excluding dual (virtual) cameras.
    const wide = this._backCameras.find(
      (d) => /wide|0\.5/i.test(d.label) && !/dual/i.test(d.label)
    )
    if (wide) return { deviceId: wide.deviceId, label: wide.label, reason: "wide-label" }

    // 3. Generic-index fallback for "camera <N>, facing back" Android labels.
    const indexed = this._backCameras
      .map((d) => {
        const m = /camera\s+(\d+)/i.exec(d.label || "")
        return m ? { deviceId: d.deviceId, label: d.label, index: Number(m[1]) } : null
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index)

    // Need at least two indexed back cameras to distinguish main from non-main.
    if (indexed.length < 2) return null

    // Lowest index = main/1x lens; the next-lowest indexed back camera is Wide.
    return { deviceId: indexed[1].deviceId, label: indexed[1].label, reason: "generic-index" }
  }

  // Capture a frame to a Blob. Draws to ONE reusable canvas sized to the
  // centre-crop of the DISPLAYED viewfinder aspect -- WYSIWYG: the saved image
  // matches exactly what the viewfinder shows under `object-fit: cover`
  // against whatever shape the fill frame ended up on this device. Prefers
  // WebP, falls back to JPEG. Rejects if no live frame.
  //
  // `aspect` is the target width/height ratio of the rendered viewfinder. When
  // omitted, it is read from the live <video> element's rendered box
  // (clientWidth / clientHeight), falling back to the native frame ratio.
  // Pass it explicitly (from the controller) when the video element's own box
  // doesn't match the displayed frame.
  //
  // Two source paths (card #3487):
  //   - "still"      -- where supportsStillCapture() is true, a real full-
  //                      resolution sensor photo via ImageCapture.takePhoto()
  //                      on the active track. Far higher fidelity than a video
  //                      frame; its native aspect is very likely NOT the
  //                      displayed viewfinder's, so it runs through the same
  //                      centre-crop math below the video-frame path always
  //                      has.
  //   - "video-frame" -- the original canvas grab off the live <video> element.
  //                      Always used on iOS (no ImageCapture at all), and used
  //                      as a silent fallback on Android/Chrome combinations
  //                      where takePhoto() itself throws (honest-degrade, same
  //                      principle as wideDeviceId() returning null rather
  //                      than guessing wrong). The caller can read
  //                      captureMethod() / stillCaptureFallbackReason() after
  //                      the call to log the outcome -- this method never
  //                      surfaces the fallback as an error.
  async captureFrame({ type = "image/webp", quality = 0.9, aspect = null } = {}) {
    const video = this._video
    if (!video || !video.videoWidth || !video.videoHeight) {
      throw new CameraStreamError("no-frame", "No live video frame to capture")
    }

    const vw = video.videoWidth
    const vh = video.videoHeight
    this._lastFrameWidth = vw
    this._lastFrameHeight = vh

    // Target aspect = the on-screen frame's actual ratio. Prefer the supplied
    // value, then the video element's rendered box, then the native frame.
    let target = Number(aspect)
    if (!Number.isFinite(target) || target <= 0) {
      const cw = video.clientWidth
      const ch = video.clientHeight
      target = cw > 0 && ch > 0 ? cw / ch : vw / vh
    }

    this._lastCaptureMethod = "video-frame"
    this._lastStillCaptureError = null

    let source = video
    let sourceWidth = vw
    let sourceHeight = vh

    if (this.supportsStillCapture()) {
      try {
        const bitmap = await this._captureStillBitmap()
        source = bitmap
        sourceWidth = bitmap.width
        sourceHeight = bitmap.height
        this._lastCaptureMethod = "still"
      } catch (err) {
        // takePhoto() (or the decode) threw -- some Android/Chrome combos have
        // partial/buggy ImageCapture support even where the API exists. Fall
        // back silently to the video-frame path rather than surface an error;
        // the controller logs stillCaptureFallbackReason() via logEvent.
        this._lastStillCaptureError = err?.message || err?.name || "unknown"
      }
    }

    // The real captured content's dimensions -- the still bitmap's when the
    // still path succeeded, else the video frame's (sourceWidth/sourceHeight
    // already default to vw/vh above). See frameDimensions() (card #3510).
    this._lastCaptureWidth = sourceWidth
    this._lastCaptureHeight = sourceHeight

    const { sx, sy, sw, sh } = this._centreCropRect(sourceWidth, sourceHeight, target)

    if (!this._canvas) this._canvas = document.createElement("canvas")
    const canvas = this._canvas
    canvas.width = Math.round(sw)
    canvas.height = Math.round(sh)
    const ctx = canvas.getContext("2d")

    // Mirror the front-facing capture to match the mirrored preview (card
    // #3485) -- the canvas is reused across captures, so reset the
    // transform first or a mirror applied here would silently carry over
    // into a later rear-camera frame. Applies identically on both paths.
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    if (this._facingMode === "user") {
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
    }
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)

    // Release the decoded still bitmap promptly -- it can be very large
    // (12MP+) and isn't needed once it's drawn to the canvas.
    if (source !== video && typeof source.close === "function") source.close()

    return this._encodeCanvas(canvas, type, quality)
  }

  // Stop the current stream and toggle facingMode. The caller restarts via
  // start() after this (matching FieldCamera.flip semantics). Flipping forgets
  // any pinned lens and resets to 1x.
  flip() {
    this.stop()
    this._facingMode = this._facingMode === "environment" ? "user" : "environment"
    this._deviceId = null
    this._mode = "1x"
    return this._facingMode
  }

  // Stop ALL tracks and detach from the video element. Safe to call repeatedly.
  stop() {
    this._stream?.getTracks().forEach((track) => track.stop())
    this._stream = null
    if (this._video) {
      try { this._video.srcObject = null } catch { /* ignore */ }
    }
  }
}
